import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { AuthInput, AuthPasswordInput, AuthButton } from "./AuthLayout";
import { login } from "../api/auth";
import { AxiosError } from "axios";
import { useAuth } from "../hooks/useAuth";

export type ERROR_TYPE = "AUTH_EMAIL_NOT_VERIFIED" | "AUTH_ACCOUNT_LOCKED" | "AUTH_INVALID_CREDENTIALS";

export function LoginForm({ onRegisterClick, onSuccess, onForgotPasswordClick }: { onRegisterClick: () => void, onSuccess: () => void, onForgotPasswordClick: () => void }) {
    const { t } = useTranslation();
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const queryClient = useQueryClient();

    const schema = z.object({
        identifier: z.string().min(1, t('auth.login.errors.identifierRequired')),
        password: z.string().min(1, t('auth.login.errors.passwordRequired')),
    });

    type FormData = z.infer<typeof schema>;

    const { register, handleSubmit, formState: { errors }, setError, watch } = useForm<FormData>({
        resolver: zodResolver(schema),
    });
    const [loading, setLoading] = useState(false);
    const [errorCode, setErrorCode] = useState<ERROR_TYPE | null>(null);
    const navigate = useNavigate();
    const identifier = watch("identifier");

    useEffect(() => {
        if (!isAuthLoading && isAuthenticated) {
            navigate({ to: "/" });
        }
    }, [isAuthenticated, isAuthLoading, navigate]);

    if (isAuthLoading) {
        return null; // Or a spinner
    }

    const onSubmit = async (data: FormData) => {
        setLoading(true);
        try {
            await login(data);
            await queryClient.invalidateQueries({ queryKey: ["auth", "user"] });
            onSuccess();
        } catch (e: any) {
            const errorMessage = e;
            const errorCode = errorMessage?.response?.data?.error?.code as ERROR_TYPE;

            if (errorCode === 'AUTH_INVALID_CREDENTIALS') {
                setError("root", {
                    message: t('auth.login.errors.invalidCredentials')
                });
            } else if (errorCode === 'AUTH_EMAIL_NOT_VERIFIED') {
                setError("root", {
                    message: t('auth.login.errors.emailNotVerified')
                });
                setErrorCode('AUTH_EMAIL_NOT_VERIFIED');
            } else if (errorCode === 'AUTH_ACCOUNT_LOCKED') {
                setError("root", {
                    message: t('auth.login.errors.accountLocked')
                });
            } else {
                setError("root", {
                    message: errorMessage.response?.data?.message || t('auth.login.errors.loginFailed')
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col items-center">
            <h1 className="font-script text-6xl text-[#338585] mb-8">{t('auth.login.title')}</h1>

            {/* Error Alert - Prominently displayed at the top */}
            {errors.root && (
                <div className="w-full mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-medium text-red-800">
                                {errors.root.message}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Verification Button */}
            {errorCode === 'AUTH_EMAIL_NOT_VERIFIED' && (
                <div className="w-full mb-6">
                    <button
                        type="button"
                        onClick={() => {
                            const email = identifier || '';
                            navigate({
                                to: '/auth/verify',
                                search: { email: email }
                            });
                        }}
                        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-[#1a9a9a] to-[#157878] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 group"
                    >
                        <svg
                            className="w-5 h-5 group-hover:scale-110 transition-transform duration-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                        </svg>
                        <span>{t('auth.login.verifyEmail')}</span>
                        <svg
                            className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 7l5 5m0 0l-5 5m5-5H6"
                            />
                        </svg>
                    </button>
                    <p className="text-xs text-gray-500 text-center mt-2">
                        {t('auth.login.verifyEmailHint')}
                    </p>
                </div>
            )}

            <AuthInput
                placeholder={t('auth.login.emailPlaceholder')}
                {...register("identifier")}
                error={errors.identifier?.message}
            />

            <AuthPasswordInput
                placeholder={t('auth.login.passwordPlaceholder')}
                {...register("password")}
                error={errors.password?.message}
            />

            <div className="w-full flex justify-end mb-4">
                <button type="button" onClick={onForgotPasswordClick} className="text-[#D85C57] text-sm hover:underline">
                    {t('auth.login.forgotPassword')}
                </button>
            </div>

            <AuthButton disabled={loading}>
                {loading ? t('auth.login.loggingIn') : t('auth.login.loginButton')}
            </AuthButton>

            <div className="mt-6 text-sm text-gray-600">
                {t('auth.login.newMember')} <button type="button" onClick={onRegisterClick} className="text-[#D85C57] hover:underline ml-1">{t('auth.login.registerNow')}</button>
            </div>
        </form>
    );
}
